import { useState } from "react";
import { ArrowLeftRight } from "lucide-react";
import {
  AppLink,
  ClickPopover,
  Dialog,
  DialogFooter,
  FailedCallout,
  PopoverHeading,
  ReasonField,
  cx,
  formatDateTime,
  useReason,
  useToast,
} from "@amber/ui";
import { carryDecisionResponseSchema, type CarryNote } from "../../contracts/index";
import { EVIDENCE_ERROR_FALLBACK, REQUIRED_REASON, clock } from "../format";
import { useEvidenceCommand } from "../queries";

export function CarryDecisionDialog({
  itemId,
  title,
  action,
  path,
  onClose,
}: {
  itemId: string;
  title: string;
  action: string;
  path: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const reason = useReason(null, REQUIRED_REASON);
  const save = useEvidenceCommand(itemId, carryDecisionResponseSchema);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={title}
      onSubmit={() => {
        if (reason.submit())
          save.mutate(
            { path, body: { reason: reason.trimmed } },
            {
              onSuccess: () => {
                toast.show(`${action}: saved as a new version`);
                onClose();
              },
            },
          );
      }}
      footer={<DialogFooter onClose={onClose} confirm={action} pending={save.isPending} />}
    >
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={save.error} fallback={EVIDENCE_ERROR_FALLBACK} />
    </Dialog>
  );
}

export function CarryNotes({
  itemId,
  notes,
  canEdit,
}: {
  itemId: string;
  notes: readonly CarryNote[];
  canEdit: boolean;
}) {
  const [deciding, setDeciding] = useState<{ note: CarryNote; decision: "apply" | "dismiss" } | null>(null);
  const onDecide = (note: CarryNote, decision: "apply" | "dismiss") => {
    setDeciding({ note, decision });
  };
  return (
    <>
      {deciding ? (
        <CarryDecisionDialog
          itemId={itemId}
          title={deciding.decision === "apply" ? `Apply “${deciding.note.now}” here…` : "Doesn't apply…"}
          action={deciding.decision === "apply" ? "Apply here" : "Doesn't apply"}
          path={`/api/evidence/carry-overs/${deciding.note.id}/${deciding.decision}`}
          onClose={() => {
            setDeciding(null);
          }}
        />
      ) : null}
      {notes.map((n) => (
        <div key={n.id} className="mt-1.5 flex items-center gap-1.5 text-md text-muted">
          <ArrowLeftRight size={12} aria-hidden="true" className="flex-none" />
          <ClickPopover
            label="A correction in another copy may apply here"
            trigger={
              <span className="cursor-pointer underline decoration-dotted underline-offset-2">
                A correction in another copy may apply here
              </span>
            }
          >
            <div className="max-w-[340px]">
              <PopoverHeading>Correction in another copy</PopoverHeading>
              <dl>
                <dt>Was</dt>
                <dd>“{n.was}”</dd>
                <dt>Now</dt>
                <dd>“{n.now}”</dd>
                <dt>Copy</dt>
                <dd>{n.from}</dd>
                <dt>By</dt>
                <dd>
                  {n.by} · {formatDateTime(n.at)}
                </dd>
                <dt>Aligned</dt>
                <dd>
                  {n.aligned
                    ? `${clock(n.aligned.range_ms[0])}–${clock(n.aligned.range_ms[1])} here · ${n.aligned.confidence === "confident" ? "confident" : "uncertain"}${n.aligned.score === null ? "" : ` (${n.aligned.score.toFixed(2)})`}`
                    : "no aligned range"}
                </dd>
              </dl>
              <p className="m-0 mt-1.5 text-md text-muted">
                Not carried over automatically: {n.condition.charAt(0).toLowerCase()}
                {n.condition.slice(1)}.
              </p>
              <div className={cx("mt-2 flex items-center gap-3")}>
                <button
                  type="button"
                  disabled={!canEdit}
                  className="text-link disabled:text-muted"
                  onClick={() => {
                    onDecide(n, "apply");
                  }}
                >
                  Apply here…
                </button>
                <button
                  type="button"
                  disabled={!canEdit}
                  className="text-link disabled:text-muted"
                  onClick={() => {
                    onDecide(n, "dismiss");
                  }}
                >
                  Doesn't apply…
                </button>
                {n.task_href ? (
                  <AppLink href={n.task_href} className="text-link">
                    Open task
                  </AppLink>
                ) : null}
              </div>
              {!canEdit ? <p className="m-0 mt-1 text-md text-muted">Only Editors and above can decide.</p> : null}
            </div>
          </ClickPopover>
        </div>
      ))}
    </>
  );
}
